import { useState, useEffect, useCallback } from "react";
import { Download, RefreshCw, Lock, LogOut, Search, Trash2, Mail, Phone, Calendar, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

const TOKEN_KEY = "mc_admin_token";

const SOURCES = [
  { key: "",                label: "All sources" },
  { key: "waitlist",        label: "Waitlist" },
  { key: "quiz",            label: "Quiz" },
  { key: "stay-connected",  label: "Stay Connected" },
  { key: "landing",         label: "Landing" },
];

export default function AdminLeads() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [countsBySource, setCountsBySource] = useState({});
  const [source, setSource] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [view, setView] = useState("leads");
  const [contactItems, setContactItems] = useState([]);
  const [contactTotal, setContactTotal] = useState(0);

  const loadContact = useCallback(async (tok) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/contact?limit=500`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setContactItems(data.items || []);
      setContactTotal(data.total || 0);
    } catch (err) {
      toast.error("Could not load contact inquiries");
    } finally {
      setLoading(false);
    }
  }, []);

  function onSwitchView(v) {
    setView(v);
    if (v === "contact" && token) loadContact(token);
    if (v === "leads" && token) load(source, token);
  }

  const load = useCallback(async (sourceFilter, tok) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sourceFilter) params.set("source", sourceFilter);
      params.set("limit", "500");
      const { data } = await api.get(`/admin/leads?${params.toString()}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setCountsBySource(data.counts_by_source || {});
      setAuthed(true);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Invalid admin token");
        sessionStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      } else {
        toast.error("Could not load leads");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(source, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onLogin(e) {
    e.preventDefault();
    if (!token.trim()) {
      toast.error("Please enter the admin token");
      return;
    }
    sessionStorage.setItem(TOKEN_KEY, token);
    load(source, token);
  }

  function onLogout() {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setItems([]);
    setTotal(0);
    setCountsBySource({});
  }

  function onChangeSource(s) {
    setSource(s);
    if (token) load(s, token);
  }

  function exportCsv() {
    const url = `${API}/admin/leads/export.csv?` +
      new URLSearchParams({ source: source || "", token }).toString();
    window.open(url, "_blank");
  }

  async function onDelete(lead) {
    if (!window.confirm(`Delete lead ${lead.email}?`)) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted");
      load(source, token);
    } catch (err) {
      toast.error("Delete failed");
    }
  }

  async function onDeleteContact(c) {
    if (!window.confirm(`Delete contact inquiry from ${c.email}?`)) return;
    try {
      await api.delete(`/admin/contact/${c.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted");
      loadContact(token);
    } catch (err) {
      toast.error("Delete failed");
    }
  }

  const filtered = items.filter((it) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (it.email || "").toLowerCase().includes(q) ||
      (it.name || "").toLowerCase().includes(q) ||
      JSON.stringify(it.extra || {}).toLowerCase().includes(q)
    );
  });

  if (!authed) return <Login token={token} setToken={setToken} onLogin={onLogin} />;

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg, #F8FCFF 0%, #FAF8F3 100%)" }}>
      <header style={{
        background: "white",
        borderBottom: "1px solid var(--mc-border-blue)",
        padding: "20px 32px",
        position: "sticky", top: 0, zIndex: 10,
      }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div className="font-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mc-text)" }}>
              Marrsing<span style={{ color: "var(--mc-blue)" }}>Clause</span>
              <sup style={{ fontSize: 10, marginLeft: 1 }}>®</sup>
              <span className="font-mono" style={{
                fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase",
                background: "rgba(111,199,242,0.18)", color: "var(--mc-blue-deep)",
                padding: "5px 12px", borderRadius: 999, marginLeft: 12, fontWeight: 600,
              }}>
                Admin · Leads
              </span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button data-testid="admin-refresh" onClick={() => load(source, token)} disabled={loading} className="mc-btn-secondary" style={{ minHeight: 42, padding: "10px 20px", fontSize: 12 }}>
              <RefreshCw size={14} className={loading ? "spin" : ""} /> {loading ? "Loading…" : "Refresh"}
            </button>
            <button data-testid="admin-export" onClick={exportCsv} className="mc-btn-primary" style={{ minHeight: 42, padding: "10px 22px", fontSize: 12 }}>
              <Download size={14} /> Export CSV
            </button>
            <button data-testid="admin-logout" onClick={onLogout} title="Logout" style={{
              background: "transparent", border: "1px solid var(--mc-border)",
              borderRadius: 999, padding: "10px 14px", cursor: "pointer",
              color: "var(--mc-text-3)", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12,
            }}>
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1400, margin: "0 auto", padding: "32px" }}>
        <div data-testid="admin-view-tabs" style={{ display: "flex", gap: 10, marginBottom: 24 }}>
          {[
            { key: "leads", label: "Leads" },
            { key: "contact", label: "Contact Inquiries" },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => onSwitchView(t.key)}
              style={{
                padding: "10px 22px", borderRadius: 999, cursor: "pointer",
                fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 600,
                letterSpacing: "0.06em", textTransform: "uppercase",
                border: view === t.key ? "1px solid var(--mc-blue)" : "1px solid var(--mc-border)",
                background: view === t.key ? "var(--mc-blue-deep)" : "white",
                color: view === t.key ? "white" : "var(--mc-text-3)",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {view === "contact" ? (
          <ContactPanel items={contactItems} total={contactTotal} loading={loading} onDelete={onDeleteContact} />
        ) : (
        <>
        {/* Stats */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 14, marginBottom: 28,
        }}>
          <StatCard label="Total leads" value={total} accent="var(--mc-blue)" testid="stat-total" />
          {SOURCES.filter((s) => s.key).map((s) => (
            <StatCard
              key={s.key}
              label={s.label}
              value={countsBySource[s.key] || 0}
              accent={s.key === "quiz" ? "var(--mc-teal)" : s.key === "stay-connected" ? "var(--mc-gold)" : "var(--mc-blue-deep)"}
              testid={`stat-${s.key}`}
            />
          ))}
        </div>

        {/* Filter bar */}
        <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: "1 1 240px" }}>
            <Search size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--mc-text-3)" }} />
            <input
              data-testid="admin-search"
              type="text"
              placeholder="Search email, name, address…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%", padding: "12px 16px 12px 42px",
                borderRadius: 12, border: "1px solid var(--mc-border)",
                fontFamily: "var(--font-sans)", fontSize: 14, color: "var(--mc-text)",
                background: "white", outline: "none", minHeight: 44,
              }}
            />
          </div>
          <div style={{ position: "relative" }}>
            <select
              data-testid="admin-source-filter"
              value={source}
              onChange={(e) => onChangeSource(e.target.value)}
              style={{
                appearance: "none",
                background: "white",
                border: "1px solid var(--mc-border)",
                borderRadius: 12,
                padding: "12px 38px 12px 16px",
                fontFamily: "var(--font-sans)", fontSize: 14,
                color: "var(--mc-text)", cursor: "pointer", minHeight: 44,
              }}
            >
              {SOURCES.map((s) => <option key={s.key || "all"} value={s.key}>{s.label}</option>)}
            </select>
            <ChevronDown size={14} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--mc-text-3)" }} />
          </div>
        </div>

        {/* Table */}
        <div style={{
          background: "white",
          borderRadius: 18,
          border: "1px solid var(--mc-border-blue)",
          boxShadow: "0 14px 36px rgba(14,10,31,0.05)",
          overflow: "hidden",
        }}>
          <div style={{ overflowX: "auto" }}>
            <table data-testid="admin-leads-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead style={{ background: "var(--mc-blue-pale)" }}>
                <tr>
                  <Th>Created</Th>
                  <Th>Source</Th>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Phone</Th>
                  <Th>Profile</Th>
                  <Th>Status</Th>
                  <Th style={{ width: 80 }}>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {loading && filtered.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 36, textAlign: "center", color: "var(--mc-text-3)" }}>Loading leads…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 48, textAlign: "center", color: "var(--mc-text-3)" }}>No leads yet.</td></tr>
                )}
                {filtered.map((lead) => {
                  const extra = lead.extra || {};
                  const isOpen = expanded === lead.id;
                  return (
                    <RowGroup key={lead.id}>
                      <tr
                        data-testid={`admin-row-${lead.id}`}
                        style={{
                          borderTop: "1px solid rgba(30,27,22,0.06)",
                          background: isOpen ? "rgba(111,199,242,0.06)" : "white",
                          cursor: "pointer",
                        }}
                        onClick={() => setExpanded(isOpen ? null : lead.id)}
                      >
                        <Td>
                          <span className="font-mono" style={{ fontSize: 12 }}>
                            {formatDate(lead.created_at)}
                          </span>
                        </Td>
                        <Td><SourceBadge source={lead.source} /></Td>
                        <Td>{lead.name || extra.first_name || "—"}</Td>
                        <Td>
                          <a href={`mailto:${lead.email}`} onClick={(e) => e.stopPropagation()} style={{ color: "var(--mc-blue-deep)", textDecoration: "none" }}>
                            {lead.email}
                          </a>
                        </Td>
                        <Td>{extra.phone || "—"}</Td>
                        <Td>{lead.profile ? <ProfileBadge profile={lead.profile} /> : "—"}</Td>
                        <Td>{extra.relationship_status || "—"}</Td>
                        <Td>
                          <button
                            data-testid={`admin-delete-${lead.id}`}
                            onClick={(e) => { e.stopPropagation(); onDelete(lead); }}
                            style={{
                              background: "transparent", border: "1px solid var(--mc-border)",
                              padding: 7, borderRadius: 10, cursor: "pointer", color: "var(--mc-text-3)",
                              display: "inline-flex", alignItems: "center",
                            }}
                            title="Delete lead"
                          >
                            <Trash2 size={13} />
                          </button>
                        </Td>
                      </tr>
                      {isOpen && (
                        <tr style={{ background: "rgba(111,199,242,0.04)" }}>
                          <td colSpan={8} style={{ padding: "16px 20px 24px" }}>
                            <ExpandedDetails lead={lead} />
                          </td>
                        </tr>
                      )}
                    </RowGroup>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <p className="font-mono" style={{ marginTop: 18, fontSize: 11.5, color: "var(--mc-text-3)", letterSpacing: "0.04em" }}>
          Showing {filtered.length} of {total} leads. Click a row for full details.
        </p>
        </>
        )}
      </main>
      <style>{`
        .spin { animation: spinme 0.8s linear infinite; }
        @keyframes spinme { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

/* ─────── LOGIN ─────── */
function Login({ token, setToken, onLogin }) {
  return (
    <div style={{
      minHeight: "100vh",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24,
      background: `
        radial-gradient(circle at 80% 12%, rgba(111,199,242,0.32), transparent 38%),
        linear-gradient(180deg, #F8FCFF 0%, #FAF8F3 100%)
      `,
    }}>
      <form onSubmit={onLogin} data-testid="admin-login-form" style={{
        maxWidth: 420, width: "100%",
        background: "rgba(255,255,255,0.96)",
        border: "1px solid var(--mc-border-blue)",
        borderRadius: 24,
        padding: "44px 36px",
        boxShadow: "0 30px 70px rgba(14,10,31,0.1)",
      }}>
        <div style={{
          width: 52, height: 52, borderRadius: "50%",
          background: "var(--mc-blue-pale)",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 22px", color: "var(--mc-blue-deep)",
        }}>
          <Lock size={22} />
        </div>
        <h1 className="font-serif" style={{ fontSize: 28, fontWeight: 500, textAlign: "center", color: "var(--mc-text)", marginBottom: 8 }}>
          Admin Access
        </h1>
        <p style={{ textAlign: "center", fontSize: 14, color: "var(--mc-text-3)", lineHeight: 1.6, marginBottom: 28 }}>
          Enter your admin token to view captured leads.
        </p>
        <label className="font-mono" style={{
          fontSize: 11, fontWeight: 500, color: "var(--mc-text-3)",
          display: "block", marginBottom: 6,
          letterSpacing: "0.06em", textTransform: "uppercase",
        }}>Admin token</label>
        <input
          data-testid="admin-token-input"
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="••••••••••••••••"
          style={{
            width: "100%", padding: "13px 16px", borderRadius: 12,
            border: "1px solid var(--mc-border)", background: "white",
            fontFamily: "var(--font-mono)", fontSize: 14, color: "var(--mc-text)",
            outline: "none", minHeight: 50, marginBottom: 18,
          }}
          autoFocus
        />
        <button data-testid="admin-login-submit" type="submit" className="mc-btn-primary" style={{ width: "100%" }}>
          Sign in
        </button>
      </form>
    </div>
  );
}

/* ─────── CONTACT PANEL ─────── */
function ContactPanel({ items, total, loading, onDelete }) {
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 24 }}>
        <StatCard label="Total inquiries" value={total} accent="var(--mc-blue-deep)" testid="stat-contact-total" />
      </div>
      <div style={{
        background: "white", borderRadius: 18, border: "1px solid var(--mc-border-blue)",
        boxShadow: "0 14px 36px rgba(14,10,31,0.05)", overflow: "hidden",
      }}>
        <div style={{ overflowX: "auto" }}>
          <table data-testid="admin-contact-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
            <thead style={{ background: "var(--mc-blue-pale)" }}>
              <tr>
                <Th>Created</Th>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Topic</Th>
                <Th>Message</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 36, textAlign: "center", color: "var(--mc-text-3)" }}>Loading inquiries…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 48, textAlign: "center", color: "var(--mc-text-3)" }}>No contact inquiries yet.</td></tr>
              )}
              {items.map((c) => (
                <tr key={c.id} data-testid={`admin-contact-row-${c.id}`} style={{ borderTop: "1px solid rgba(30,27,22,0.06)" }}>
                  <Td><span className="font-mono" style={{ fontSize: 12 }}>{formatDate(c.created_at)}</span></Td>
                  <Td>{c.name || "—"}</Td>
                  <Td>
                    <a href={`mailto:${c.email}`} style={{ color: "var(--mc-blue-deep)", textDecoration: "none" }}>{c.email}</a>
                  </Td>
                  <Td>
                    <span className="font-mono" style={{
                      fontSize: 10.5, padding: "4px 10px", borderRadius: 999,
                      background: "rgba(111,199,242,0.16)", color: "var(--mc-blue-deep)",
                      letterSpacing: "0.04em", fontWeight: 500,
                    }}>{c.topic || "—"}</span>
                  </Td>
                  <Td style={{ maxWidth: 420, whiteSpace: "pre-wrap", color: "var(--mc-text-2)" }}>{c.message || "—"}</Td>
                  <Td>
                    <button
                      data-testid={`admin-delete-contact-${c.id}`}
                      onClick={() => onDelete(c)}
                      style={{
                        background: "transparent", border: "1px solid var(--mc-border)",
                        padding: 7, borderRadius: 10, cursor: "pointer", color: "var(--mc-text-3)",
                        display: "inline-flex", alignItems: "center",
                      }}
                      title="Delete contact inquiry"
                    >
                      <Trash2 size={13} />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="font-mono" style={{ marginTop: 18, fontSize: 11.5, color: "var(--mc-text-3)", letterSpacing: "0.04em" }}>
        Showing {items.length} of {total} contact inquiries.
      </p>
    </div>
  );
}

/* ─────── SHARED ─────── */
function RowGroup({ children }) {
  // tbody-safe wrapper using React.Fragment-like behavior — must return only <tr> children
  return <>{children}</>;
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} style={{
      background: "white",
      border: "1px solid var(--mc-border-blue)",
      borderRadius: 18,
      padding: "20px 22px",
      position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: 0, left: 0, width: 3, height: "100%", background: accent }} />
      <div className="font-mono" style={{
        fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase",
        color: "var(--mc-text-3)", marginBottom: 6,
      }}>{label}</div>
      <div className="font-serif" style={{ fontSize: 36, fontWeight: 500, color: "var(--mc-text)", lineHeight: 1 }}>{value}</div>
    </div>
  );
}

function Th({ children, style }) {
  return (
    <th style={{
      textAlign: "left", padding: "14px 18px",
      fontFamily: "var(--font-mono)", fontSize: 10.5,
      letterSpacing: "0.1em", textTransform: "uppercase",
      color: "var(--mc-blue-deep)", fontWeight: 600, ...style,
    }}>{children}</th>
  );
}
function Td({ children, style }) {
  return (
    <td style={{
      padding: "14px 18px", fontSize: 13.5,
      color: "var(--mc-text-2)", verticalAlign: "middle", ...style,
    }}>{children}</td>
  );
}

function SourceBadge({ source }) {
  const styles = {
    quiz:             { bg: "#EAF8F5", color: "#317F70" },
    waitlist:         { bg: "#EEF8FC", color: "#2C8FB7" },
    "stay-connected": { bg: "#F8EED8", color: "#9A7326" },
    landing:          { bg: "#F2EDE4", color: "#5A5750" },
  };
  const s = styles[source] || { bg: "#EEEAE0", color: "#5A5750" };
  return (
    <span className="font-mono" style={{
      fontSize: 10.5, padding: "4px 10px", borderRadius: 999,
      background: s.bg, color: s.color,
      letterSpacing: "0.06em", fontWeight: 500, textTransform: "uppercase",
    }}>{source || "unknown"}</span>
  );
}

function ProfileBadge({ profile }) {
  const colors = { clarity: "#4AAED8", communication: "#7FCDBB", alignment: "#D7B56D" };
  return (
    <span className="font-mono" style={{
      fontSize: 11, color: colors[profile] || "var(--mc-text-3)",
      textTransform: "capitalize", fontWeight: 500,
    }}>{profile}</span>
  );
}

function ExpandedDetails({ lead }) {
  const extra = lead.extra || {};
  const clicks = lead.clicks || {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 18, fontSize: 13 }}>
      <Detail icon={<Mail size={13} />} label="Email" value={lead.email} />
      {extra.phone && <Detail icon={<Phone size={13} />} label="Phone" value={extra.phone} />}
      {extra.address && <Detail label="Address" value={extra.address} />}
      {extra.anniversary_date && <Detail icon={<Calendar size={13} />} label="Anniversary" value={extra.anniversary_date} />}
      {extra.relationship_status && <Detail label="Status" value={extra.relationship_status} />}
      {extra.relationship_stage && <Detail label="Relationship Stage" value={extra.relationship_stage} />}
      {lead.profile && <Detail label="Quiz profile" value={lead.profile} />}
      {lead.scores && (
        <Detail label="Scores" value={
          <span>C {lead.scores.Clarity}% · Co {lead.scores.Communication}% · A {lead.scores.Alignment}%</span>
        } />
      )}
      {lead.tags && lead.tags.length > 0 && (
        <div style={{ gridColumn: "1 / -1" }}>
          <div className="font-mono" style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--mc-text-3)", marginBottom: 6 }}>Tags</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {lead.tags.map((t) => (
              <span key={t} className="font-mono" style={{
                fontSize: 10.5, padding: "3px 10px", borderRadius: 999,
                background: t === "High Intent" ? "rgba(215,181,109,0.25)" : "rgba(111,199,242,0.16)",
                color: t === "High Intent" ? "#9A7326" : "var(--mc-blue-deep)",
              }}>{t}</span>
            ))}
          </div>
        </div>
      )}
      {Object.keys(clicks).length > 0 && (
        <Detail label="Click counts" value={
          <span>{Object.entries(clicks).map(([k,v]) => `${k}:${v}`).join(" · ")}</span>
        } />
      )}
      <Detail label="Lead ID" value={<code style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>{lead.id}</code>} />
    </div>
  );
}

function Detail({ icon, label, value }) {
  return (
    <div>
      <div className="font-mono" style={{
        fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
        color: "var(--mc-text-3)", marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 4,
      }}>{icon}{label}</div>
      <div style={{ color: "var(--mc-text)", fontSize: 13.5 }}>{value || "—"}</div>
    </div>
  );
}

function formatDate(d) {
  if (!d) return "—";
  try {
    const date = new Date(d);
    return date.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return String(d);
  }
}
